<template>
    <div class="explorer-layout">
        <!-- ===== LEFT PANEL: Navigation ===== -->
        <aside class="explorer-sidebar">
            <div class="sidebar-section">
                <div class="sidebar-section-title">Разделы</div>
                <button
                    class="sidebar-nav-item"
                    :class="{ active: currentSectionKey === 'all' }"
                    @click="navigateToSection('all')"
                >
                    <i class="pi pi-th-large"></i>
                    <span>Все проекты</span>
                </button>
                <button
                    class="sidebar-nav-item"
                    :class="{ active: currentSectionKey === 'recent' }"
                    @click="navigateToSection('recent')"
                >
                    <i class="pi pi-clock"></i>
                    <span>Недавние</span>
                </button>
                <button
                    class="sidebar-nav-item"
                    :class="{ active: currentSectionKey === 'favorites' }"
                    @click="navigateToSection('favorites')"
                >
                    <i class="pi pi-star-fill"></i>
                    <span>Избранное</span>
                </button>
                <button
                    class="sidebar-nav-item"
                    :class="{ active: currentSectionKey === 'root' }"
                    @click="navigateToSection('root')"
                >
                    <i class="pi pi-folder-open"></i>
                    <span>Корневая папка</span>
                </button>
            </div>

            <div class="sidebar-section">
                <div class="sidebar-section-title sidebar-section-title-row">
                    <span>Папки</span>
                    <button class="sidebar-small-btn" title="Создать папку" @click="openCreateFolderDialog">
                        <i class="pi pi-plus"></i>
                    </button>
                </div>
                <div class="sidebar-folder-list" v-if="folderTree.length">
                    <FolderTreeItem
                        v-for="folder in folderTree"
                        :key="'f-' + folder.id"
                        :folder="folder"
                        :active-folder-id="currentFolderId"
                        :depth="0"
                        @select="navigateToFolder"
                        @create-subfolder="(parent) => openCreateFolderDialog(parent)"
                    />
                </div>
                <div v-else class="sidebar-empty">Нет папок</div>
            </div>

            <div class="sidebar-spacer"></div>

            <div class="sidebar-actions">
                <PrimeButton
                    icon="pi pi-folder"
                    label="Новая папка"
                    class="p-button-sm p-button-outlined sidebar-action-btn"
                    @click="openCreateFolderDialog"
                />
            </div>
        </aside>

        <!-- ===== RIGHT PANEL: Content ===== -->
        <main class="explorer-content">
            <!-- Breadcrumbs -->
            <nav class="breadcrumbs" aria-label="Breadcrumb">
                <span class="breadcrumb-item breadcrumb-home" @click="navigateToSection('all')">
                    <i class="pi pi-home"></i> Проекты
                </span>
                <template v-for="(crumb, idx) in breadcrumbs" :key="idx">
                    <span class="breadcrumb-separator">/</span>
                    <span
                        class="breadcrumb-item"
                        :class="{ 'breadcrumb-active': idx === breadcrumbs.length - 1 }"
                        @click="crumb.action"
                    >
                        {{ crumb.label }}
                    </span>
                </template>
            </nav>

            <!-- Toolbar -->
            <div class="explorer-toolbar">
                <div class="toolbar-search">
                    <i class="pi pi-search search-icon"></i>
                    <input
                        ref="searchInput"
                        v-model="searchQuery"
                        type="text"
                        class="search-input"
                        placeholder="Поиск проектов..."
                        @input="onSearchInput"
                        @keydown.escape="clearSearch"
                    />
                    <button v-if="searchQuery" class="search-clear" @click="clearSearch" title="Очистить поиск">
                        <i class="pi pi-times"></i>
                    </button>
                </div>

                <div class="toolbar-actions">
                    <PrimeButton
                        icon="pi pi-file"
                        label="Новый проект"
                        class="p-button-sm"
                        @click="addProject"
                    />
                    <PrimeButton
                        icon="pi pi-folder"
                        label="Новая папка"
                        class="p-button-sm p-button-outlined"
                        @click="openCreateFolderDialog"
                    />
                    <div class="view-toggle">
                        <PrimeButton
                            icon="pi pi-list"
                            class="p-button-sm p-button-text"
                            :class="{ 'p-button-primary': viewMode === 'list' }"
                            @click="viewMode = 'list'"
                            v-tooltip.top="'Список'"
                        />
                        <PrimeButton
                            icon="pi pi-th-large"
                            class="p-button-sm p-button-text"
                            :class="{ 'p-button-primary': viewMode === 'tiles' }"
                            @click="viewMode = 'tiles'"
                            v-tooltip.top="'Плитка'"
                        />
                    </div>
                </div>
            </div>

            <!-- Section Header -->
            <div class="section-header">
                <h2 class="section-title">{{ sectionTitle }}</h2>
                <span class="section-count" v-if="!isSearchMode">{{ filteredProjects.length }} проектов</span>
                <span class="section-count" v-else>Найдено: {{ filteredProjects.length }}</span>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="explorer-loading">
                <i class="pi pi-spin pi-spinner"></i> Загрузка...
            </div>

            <!-- Empty state -->
            <div v-else-if="filteredProjects.length === 0 && !searchQuery" class="explorer-empty">
                <i class="pi pi-folder-open empty-icon"></i>
                <p v-if="currentSectionKey === 'root'">В корневой папке нет проектов</p>
                <p v-else-if="currentSectionKey === 'recent'">Нет недавних проектов</p>
                <p v-else>Нет проектов. Создайте первый проект!</p>
            </div>

            <!-- Search empty -->
            <div v-else-if="filteredProjects.length === 0 && searchQuery" class="explorer-empty">
                <i class="pi pi-search empty-icon"></i>
                <p>Ничего не найдено по запросу «{{ searchQuery }}»</p>
            </div>

            <!-- ===== LIST VIEW ===== -->
            <div v-else-if="viewMode === 'list'" class="list-view">
                <div class="list-header">
                    <span class="list-cell list-cell-favorite"></span>
                    <span class="list-cell list-cell-preview"></span>
                    <span class="list-cell list-cell-name">Название</span>
                    <span class="list-cell list-cell-path">Путь</span>
                    <span class="list-cell list-cell-date">Изменён</span>
                    <span class="list-cell list-cell-source">Источник</span>
                    <span class="list-cell list-cell-public">Доступ</span>
                    <span class="list-cell list-cell-records">Записи</span>
                    <span class="list-cell list-cell-actions">Действия</span>
                </div>
                <div
                    v-for="project in filteredProjects"
                    :key="project.id"
                    class="list-row"
                    :class="{ 'list-row-selected': selectedProject?.id === project.id }"
                    @click="openProject(project)"
                >
                    <span class="list-cell list-cell-favorite" @click.stop>
                        <button
                            type="button"
                            class="favorite-btn"
                            :class="{ 'favorite-btn-active': isFavoriteProject(project.id) }"
                            :title="isFavoriteProject(project.id) ? 'Убрать из избранного' : 'Добавить в избранное'"
                            @click="toggleFavorite(project)"
                        >
                            <i :class="isFavoriteProject(project.id) ? 'pi pi-star-fill' : 'pi pi-star'"></i>
                        </button>
                    </span>
                    <span class="list-cell list-cell-preview">
                        <img
                            v-if="project.template_background_file && !previewErrors[project.id]"
                            :src="previewUrl(project)"
                            :class="orientationClass(project.template_orientation)"
                            class="list-preview-img"
                            alt=""
                            loading="lazy"
                            @error="onPreviewError(project.id)"
                        />
                        <div v-else class="list-preview-placeholder">
                            <i class="pi pi-image"></i>
                        </div>
                    </span>
                    <span class="list-cell list-cell-name">
                        <span class="project-name-text" :title="project.name">{{ project.name }}</span>
                    </span>
                    <span class="list-cell list-cell-path" :title="project.folder_path || '—'">
                        {{ project.folder_path || '—' }}
                    </span>
                    <span class="list-cell list-cell-date">{{ formatDate(project.updated_at) }}</span>
                    <span class="list-cell list-cell-source">
                        <PrimeTag
                            v-if="project.data_source"
                            :value="sourceLabel(project.data_source)"
                            :severity="sourceSeverity(project.data_source)"
                            class="source-tag"
                        />
                        <span v-else class="source-none">—</span>
                    </span>
                    <span class="list-cell list-cell-public">
                        <i
                            v-if="project.public"
                            class="pi pi-globe public-yes"
                            title="Публичный"
                        ></i>
                        <i v-else class="pi pi-lock public-no" title="Приватный"></i>
                    </span>
                    <span class="list-cell list-cell-records">{{ project.records_count ?? '—' }}</span>
                    <span class="list-cell list-cell-actions" @click.stop>
                        <PrimeButton
                            icon="pi pi-ellipsis-v"
                            class="p-button-rounded p-button-text p-button-sm action-menu-btn"
                            @click="openProjectMenu($event, project)"
                        />
                    </span>
                </div>
            </div>

            <!-- ===== TILE VIEW ===== -->
            <div v-else class="tile-view">
                <div
                    v-for="project in filteredProjects"
                    :key="'t-' + project.id"
                    class="tile-card"
                    :class="{ 'tile-card-selected': selectedProject?.id === project.id }"
                    @click="openProject(project)"
                >
                    <div class="tile-preview">
                        <img
                            v-if="project.template_background_file && !previewErrors[project.id]"
                            :src="previewUrl(project)"
                            :class="orientationClass(project.template_orientation)"
                            alt=""
                            loading="lazy"
                            @error="onPreviewError(project.id)"
                        />
                        <div v-else class="tile-placeholder">
                            <i class="pi pi-image"></i>
                            <span>{{ project.template_background_file ? 'Ошибка загрузки' : 'Нет фона' }}</span>
                        </div>
                    </div>
                    <div class="tile-info">
                        <div class="tile-name" :title="project.name">{{ project.name }}</div>
                        <div class="tile-meta">
                            <span class="tile-path" :title="project.folder_path || 'Корневая папка'">
                                {{ project.folder_path || 'Корневая папка' }}
                            </span>
                            <span class="tile-date">{{ formatDate(project.updated_at) }}</span>
                        </div>
                        <div class="tile-badges">
                            <PrimeTag
                                v-if="project.data_source"
                                :value="sourceLabel(project.data_source)"
                                :severity="sourceSeverity(project.data_source)"
                                class="source-tag-sm"
                            />
                            <i
                                v-if="project.public"
                                class="pi pi-globe public-yes-sm"
                                title="Публичный"
                            ></i>
                        </div>
                    </div>
                    <button
                        type="button"
                        class="tile-favorite-btn"
                        :class="{ 'tile-favorite-btn-active': isFavoriteProject(project.id) }"
                        :title="isFavoriteProject(project.id) ? 'Убрать из избранного' : 'Добавить в избранное'"
                        @click.stop="toggleFavorite(project)"
                    >
                        <i :class="isFavoriteProject(project.id) ? 'pi pi-star-fill' : 'pi pi-star'"></i>
                    </button>
                    <PrimeButton
                        icon="pi pi-ellipsis-v"
                        class="p-button-rounded p-button-text p-button-sm tile-action-btn"
                        @click.stop="openProjectMenu($event, project)"
                    />
                </div>
            </div>
        </main>

        <!-- ===== Context Menu for Projects ===== -->
        <ContextMenu ref="menuProject" :model="projectContextItems" />

        <!-- ===== Context Menu for Folders ===== -->
        <ContextMenu ref="menuFolder" :model="folderContextItems" />

        <!-- ===== Create Project Dialog ===== -->
        <PrimeDialog
            header="Создать новый проект"
            v-model:visible="showDialogForm"
            :breakpoints="{'960px': '75vw', '640px': '90vw'}"
            :style="{width: '50vw'}"
        >
            <p>Введите название проекта</p>
            <InputText v-model="newProject.name" placeholder="введите название проекта" class="w-full"></InputText>
            <p>Ориентация</p>
            <PrimeDropdown
                v-model="newProject.template.orientation"
                @change="onOrientationChange"
                class="p-dropdown-sm"
                optionValue="value"
                optionLabel="label"
                :options="orientations"
            />
            <p>Размер изображения: {{ imageSize }}</p>

            <PrimePanel header="Шаблон проекта" class="mt-3">
                <div class="flex py-2">
                    <div class="preview">
                        <img :src="templateImg" alt="" v-if="templateImg !== null">
                        <div v-if="blankBackground" class="blank-bg-hint">Без фона.<br>Фон можно будет сгенерировать AI-помощником в редакторе проекта.</div>
                    </div>
                    <div class="ml-2">
                        <PrimeButton @click="selectTemplateBg" class="mr-3 p-button-sm">Выбрать из библиотеки</PrimeButton>
                        <FileUpload
                            class="p-button-sm mt-2"
                            @select="selectFile"
                            mode="basic"
                            name="demo[]"
                            custom-upload
                            upload="upload"
                            :multiple="false"
                            accept="image/*"
                            :maxFileSize="1000000"
                            :show-cancel-button="false"
                            :show-upload-button="false"
                            chooseLabel="Свой файл"
                        ></FileUpload>
                        <div>
                            <PrimeButton
                                @click="selectBlankBg"
                                class="p-button-sm mt-2"
                                :class="blankBackground ? '' : 'p-button-outlined'"
                            >Без фона</PrimeButton>
                        </div>
                    </div>
                </div>
            </PrimePanel>

            <template #footer>
                <PrimeButton label="Отмена" icon="pi pi-times" @click="closeDialog" class="p-button-text"/>
                <PrimeButton label="Создать" icon="pi pi-check" @click="createProject" autofocus />
            </template>
        </PrimeDialog>

        <!-- ===== Create Folder Dialog ===== -->
        <ConfirmDialog group="createFolderDialog">
            <template #message="">
                <div class="flex p-4">
                    <InputText v-model="newFolderName" placeholder="Название папки"></InputText>
                </div>
            </template>
        </ConfirmDialog>

        <!-- ===== Rename Folder Dialog ===== -->
        <ConfirmDialog group="renameFolderDialog">
            <template #message="">
                <div class="flex p-4">
                    <InputText v-model="newFolderName" placeholder="Название папки"></InputText>
                </div>
            </template>
        </ConfirmDialog>

        <!-- ===== Delete Folder Dialog ===== -->
        <ConfirmDialog group="deleteFolderDialog"></ConfirmDialog>

        <!-- ===== Move Project Dialog ===== -->
        <ConfirmDialog group="replaceProjectDialog">
            <template #message="">
                <div class="flex p-4">
                    <TreeSelect
                        v-model="replaceFolderId"
                        :options="folderSelectOptions"
                        placeholder="Выберите папку"
                        class="md:w-20rem w-full"
                    />
                </div>
            </template>
        </ConfirmDialog>

        <!-- ===== Delete Project Dialog ===== -->
        <ConfirmDialog></ConfirmDialog>

        <!-- ===== Template BG Selector ===== -->
        <TemplateBGSelector
            class="my-bg"
            v-show="templateSelectorVisible"
            @select="onSelectBG"
            @close="templateSelectorVisible = false"
        />

        <!-- ===== Background Crop Dialog ===== -->
        <BackgroundCropDialog
            v-model="showCropDialog"
            :imageSrc="cropImageSrc"
            :imageWidth="cropImageWidth"
            :imageHeight="cropImageHeight"
            :orientation="newProject.template.orientation"
            :filename="cropFilename"
            @apply="onCropApply"
            @cancel="onCropCancel"
        />
    </div>
</template>

<script setup>
import api from "@/libs/api";
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";
import { debounce } from "@/libs/utils";
import { useRouter } from 'vue-router'
import { useConfirm } from "primevue/useconfirm";
import TemplateBGSelector from "@/components/TemplateBGSelector";
import BackgroundCropDialog from "@/components/BackgroundCropDialog";
import FolderTreeItem from "@/components/FolderTreeItem";
import ContextMenu from 'primevue/contextmenu';
import EventBus from "@/libs/event-bus";
import events from "@/libs/events";
import projectShortcuts from "@/libs/projectShortcuts";

// ===== Refs =====
const router = useRouter();
const confirm = useConfirm();

const loading = ref(false);
const searchQuery = ref('');
const searchInput = ref(null);

// Explorer data
const allProjects = ref([]);
const folderTree = ref([]);
const foldersFlat = ref({});

// Navigation
const currentSectionKey = ref('all'); // 'all', 'recent', 'root', or 'folder:{id}'
const currentFolderId = ref(null);

// View mode
const viewMode = ref(localStorage.getItem('explorer_view_mode') || 'list');

// Selection
const selectedProject = ref(null);

// Preview errors tracking
const previewErrors = ref({});

// Favorites (reactive — synced with projectShortcuts via PROJECT_SHORTCUTS_UPDATED)
const favoriteIds = ref(new Set());

function refreshFavorites() {
    favoriteIds.value = new Set(
        projectShortcuts.getFavoriteProjects().map(p => String(p.id))
    );
}

refreshFavorites();

// Menu
const menuProject = ref();
const menuFolder = ref();
const contextFolder = ref(null);

// Dialogs state (preserved from original)
const showDialogForm = ref(false);
const newFolderName = ref('');
const replaceFolderId = ref();
const selectedFolder = ref(null);
const uploadedFiles = ref({});
const templateSelectorVisible = ref(false);
const selectedTemplate = ref({});
const blankBackground = ref(false);
const showCropDialog = ref(false);
const cropImageSrc = ref('');
const cropImageWidth = ref(0);
const cropImageHeight = ref(0);
const cropFilename = ref('');
const cropProjectId = ref(null);
const pendingUploadData = ref(null);

// ===== Computed =====

const currentSectionTitle = computed(() => {
    switch (currentSectionKey.value) {
        case 'all': return 'Все проекты';
        case 'recent': return 'Недавние';
        case 'favorites': return 'Избранное';
        case 'root': return 'Корневая папка';
        default:
            if (currentSectionKey.value.startsWith('folder:')) {
                const id = parseInt(currentSectionKey.value.replace('folder:', ''));
                const folder = foldersFlat.value[id];
                return folder ? folder.name : 'Папка';
            }
            return 'Проекты';
    }
});

const isSearchMode = computed(() => searchQuery.value.trim().length > 0);

const sectionTitle = computed(() => {
    if (isSearchMode.value) {
        return `Поиск: «${searchQuery.value}»`;
    }
    return currentSectionTitle.value;
});

const breadcrumbs = computed(() => {
    const crumbs = [];

    if (isSearchMode.value) return crumbs;

    if (currentSectionKey.value === 'all') {
        return [];
    }

    if (currentSectionKey.value === 'recent') {
        crumbs.push({ label: 'Все проекты', action: () => navigateToSection('all') });
        crumbs.push({ label: 'Недавние', action: () => {} });
        return crumbs;
    }

    if (currentSectionKey.value === 'favorites') {
        crumbs.push({ label: 'Все проекты', action: () => navigateToSection('all') });
        crumbs.push({ label: 'Избранное', action: () => {} });
        return crumbs;
    }

    if (currentSectionKey.value === 'root') {
        crumbs.push({ label: 'Все проекты', action: () => navigateToSection('all') });
        crumbs.push({ label: 'Корневая папка', action: () => {} });
        return crumbs;
    }

    if (currentSectionKey.value.startsWith('folder:')) {
        crumbs.push({ label: 'Все проекты', action: () => navigateToSection('all') });

        const id = parseInt(currentSectionKey.value.replace('folder:', ''));
        const path = resolveFolderPath(id);

        for (let i = 0; i < path.length; i++) {
            const folderId = path[i].id;
            crumbs.push({
                label: path[i].name,
                action: i < path.length - 1
                    ? () => navigateToFolder(folderId)
                    : () => {}
            });
        }
    }

    return crumbs;
});

const filteredProjects = computed(() => {
    if (isSearchMode.value) {
        const q = searchQuery.value.toLowerCase().trim();
        if (!q) return [];
        return allProjects.value.filter(p =>
            p.name.toLowerCase().includes(q) ||
            (p.folder_path && p.folder_path.toLowerCase().includes(q))
        );
    }

    switch (currentSectionKey.value) {
        case 'all':
            return allProjects.value;
        case 'recent': {
            const recentIds = projectShortcuts.getRecentProjects().map(r => r.id);
            return allProjects.value.filter(p => recentIds.includes(p.id));
        }
        case 'favorites':
            return allProjects.value.filter(p => favoriteIds.value.has(String(p.id)));
        case 'root':
            return allProjects.value.filter(p => !p.folder_id);
        default:
            if (currentSectionKey.value.startsWith('folder:')) {
                const id = parseInt(currentSectionKey.value.replace('folder:', ''));
                return allProjects.value.filter(p => p.folder_id === id);
            }
            return [];
    }
});

// Folder select options for the move dialog (tree format)
const folderSelectOptions = computed(() => {
    const buildOptions = (nodes) => {
        return nodes.map(f => ({
            key: f.id,
            label: f.name,
            children: f.children && f.children.length ? buildOptions(f.children) : undefined
        }));
    };

    const options = buildOptions(folderTree.value);
    options.unshift({
        key: null,
        label: 'Корневая директория',
        icon: 'pi pi-fw pi-folder',
    });
    return options;
});

// ===== Folder context menu items =====
const folderContextItems = computed(() => [
    {
        label: 'Создать подпапку',
        icon: 'pi pi-fw pi-folder',
        command: () => {
            if (contextFolder.value) openCreateFolderDialog(contextFolder.value);
        }
    },
    {
        label: 'Переименовать',
        icon: 'pi pi-fw pi-pencil',
        command: () => {
            if (contextFolder.value) openRenameFolderDialog(contextFolder.value);
        }
    },
    {
        label: 'Удалить',
        icon: 'pi pi-fw pi-trash',
        command: () => {
            if (contextFolder.value) openDeleteFolderDialog(contextFolder.value);
        }
    }
]);

// ===== Project context menu items =====
const projectContextItems = computed(() => [
    {
        label: 'Открыть',
        icon: 'pi pi-fw pi-external-link',
        command: () => { if (selectedProject.value) openProject(selectedProject.value); }
    },
    {
        label: 'Копировать',
        icon: 'pi pi-fw pi-copy',
        command: () => { if (selectedProject.value) copyProject(selectedProject.value); }
    },
    {
        label: selectedProject.value && isFavoriteProject(selectedProject.value.id)
            ? 'Убрать из избранного'
            : 'Добавить в избранное',
        icon: selectedProject.value && isFavoriteProject(selectedProject.value.id)
            ? 'pi pi-fw pi-star-fill'
            : 'pi pi-fw pi-star',
        command: () => { if (selectedProject.value) toggleFavorite(selectedProject.value); }
    },
    {
        label: 'Переместить',
        icon: 'pi pi-fw pi-directions',
        command: () => { if (selectedProject.value) openMoveProjectDialog(selectedProject.value); }
    },
    {
        label: 'Удалить',
        icon: 'pi pi-fw pi-trash',
        command: () => { if (selectedProject.value) deleteProject(selectedProject.value); }
    }
]);

// ===== Methods =====

// Navigation
function navigateToSection(key) {
    currentSectionKey.value = key;
    if (key.startsWith('folder:')) {
        currentFolderId.value = parseInt(key.replace('folder:', ''));
    } else {
        currentFolderId.value = null;
    }
    clearSearch();
}

function navigateToFolder(folderId) {
    currentSectionKey.value = 'folder:' + folderId;
    currentFolderId.value = folderId;
    clearSearch();
}

function resolveFolderPath(folderId) {
    const path = [];
    let current = folderId;
    let safety = 0;
    while (current && safety < 20) {
        const folder = foldersFlat.value[current];
        if (!folder) break;
        path.unshift({ id: folder.id, name: folder.name });
        current = folder.parent_id;
        safety++;
    }
    return path;
}

// Server-side search (debounced, for large archives)
const serverSearch = debounce(function(query) {
    if (query.trim().length < 2) return;
    api.searchProjects(query).then(data => {
        if (data.query === searchQuery.value && data.projects) {
            // Replace allProjects with server results during search,
            // so navigation sections show correct filtered set.
            // We keep a backup of the full list to restore on clear.
            if (!_fullProjectsBackup.value) {
                _fullProjectsBackup.value = [...allProjects.value];
            }
            allProjects.value = data.projects;
        }
    }).catch(() => {
        // Fallback: client-side filter still works via computed
    });
}, 400);

const _fullProjectsBackup = ref(null);

function onSearchInput() {
    if (searchQuery.value.trim().length >= 2) {
        serverSearch(searchQuery.value);
    }
}

function clearSearch() {
    searchQuery.value = '';
    if (searchInput.value) {
        searchInput.value.blur();
    }
    // Restore full project list
    if (_fullProjectsBackup.value) {
        allProjects.value = _fullProjectsBackup.value;
        _fullProjectsBackup.value = null;
    }
}

// View mode
watch(viewMode, (val) => {
    localStorage.setItem('explorer_view_mode', val);
});

// Formatting
function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const ye = new Intl.DateTimeFormat('ru', { year: 'numeric' }).format(d);
    const mo = new Intl.DateTimeFormat('ru', { month: 'short' }).format(d);
    const da = new Intl.DateTimeFormat('ru', { day: '2-digit' }).format(d);
    const ho = new Intl.DateTimeFormat('ru', { hour: '2-digit' }).format(d);
    const mi = new Intl.DateTimeFormat('ru', { minute: '2-digit' }).format(d);
    return `${da} ${mo} ${ye} ${ho}:${mi}`;
}

function sourceLabel(source) {
    return source === 'api' ? 'API' : source === 'manual' ? 'Ручной' : source;
}

function sourceSeverity(source) {
    return source === 'api' ? 'info' : source === 'manual' ? 'success' : 'warn';
}

function orientationClass(orientation) {
    return orientation === 'L' ? 'tile-img-landscape' : 'tile-img-portrait';
}

function previewUrl(project) {
    if (!project.template_background_file || !project.guid) return '';
    return `${api.server}/projects/${project.guid}/images/${project.template_background_file}`;
}

function onPreviewError(projectId) {
    previewErrors.value[projectId] = true;
}

function isFavoriteProject(id) {
    return favoriteIds.value.has(String(id));
}

function toggleFavorite(project) {
    if (isFavoriteProject(project.id)) {
        projectShortcuts.removeFavoriteProject(project.id);
    } else {
        projectShortcuts.addFavoriteProject(project);
    }
    refreshFavorites();
    EventBus.$emit(events.PROJECT_SHORTCUTS_UPDATED);
}

// Project actions
function openProject(project) {
    router.push({ name: 'project_edit', params: { project_id: project.id } });
}

function openProjectMenu(event, project) {
    selectedProject.value = project;
    nextTick(() => {
        if (menuProject.value) {
            menuProject.value.show(event);
        }
    });
}

function copyProject(project) {
    api.copyProject(project.id).then(() => loadData());
}

function openMoveProjectDialog(project) {
    selectedProject.value = project;
    replaceFolderId.value = null;
    confirm.require({
        group: 'replaceProjectDialog',
        header: 'Переместить проект',
        icon: 'pi pi-info-circle',
        acceptClass: 'p-button-primary',
        acceptLabel: 'Переместить',
        rejectLabel: 'Отмена',
        accept: () => {
            const targetFolderId = replaceFolderId.value
                ? Object.entries(replaceFolderId.value)[0]?.[0] ?? null
                : null;
            api.replaceProject(selectedProject.value.id, targetFolderId || undefined)
                .then(() => loadData());
        },
        reject: () => {}
    });
}

function deleteProject(project) {
    selectedProject.value = project;
    confirm.require({
        message: 'Вы действительно хотите удалить проект «' + project.name + '»?',
        header: 'Подтвердите удаление',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        acceptLabel: 'Удалить',
        rejectLabel: 'Отмена',
        accept: () => {
            api.deleteProject(selectedProject.value.id).then(() => loadData());
        },
        reject: () => {}
    });
}

// Folder actions
function openCreateFolderDialog(parentFolder) {
    selectedFolder.value = parentFolder ? { id: parentFolder.id, name: parentFolder.name } : null;
    newFolderName.value = '';
    confirm.require({
        group: 'createFolderDialog',
        header: 'Создать папку',
        icon: 'pi pi-info-circle',
        acceptClass: 'p-button-primary',
        acceptLabel: 'Создать',
        rejectLabel: 'Отмена',
        accept: () => {
            const parentId = selectedFolder.value?.id ?? null;
            api.createFolder(newFolderName.value, parentId).then(() => loadData());
        },
        reject: () => {}
    });
}

// ===== New project dialog (preserved from original) =====

const orientations = ref([
    {label: 'Портретная', value: 'P'},
    {label: 'Альбомная', value: 'L'},
]);

const newProjectObjTemplate = {
    name: '',
    template: {
        orientation: 'L',
        unit: 'mm',
        size: 'A4',
        width: 297,
        height: 210,
        background_file: null,
        blocks: "[]"
    }
};

const newProject = ref({...newProjectObjTemplate});

const templateImg = computed(() => {
    if (selectedTemplate.value?.preview) return "/backgrounds/" + selectedTemplate.value.preview;
    if (uploadedFiles.value[0] !== undefined) return URL.createObjectURL(uploadedFiles.value[0]);
    return null;
});

const imageSize = computed(() =>
    newProject.value.template.orientation === 'L' ? '1126 x 796' : '796 x 1126'
);

function onOrientationChange() {
    newProject.value.template.width = newProject.value.template.orientation === 'L' ? 297 : 210;
    newProject.value.template.height = newProject.value.template.orientation === 'L' ? 210 : 297;
}

function selectTemplateBg() {
    blankBackground.value = false;
    templateSelectorVisible.value = true;
}

function selectFile(e) {
    blankBackground.value = false;
    uploadedFiles.value = e.files;
    pendingUploadData.value = null;
}

function selectBlankBg() {
    blankBackground.value = true;
    selectedTemplate.value = {};
    uploadedFiles.value = {};
    pendingUploadData.value = null;
}

function onSelectBG(item) {
    blankBackground.value = false;
    selectedTemplate.value = item;
    uploadedFiles.value = {};
    pendingUploadData.value = null;
    templateSelectorVisible.value = false;
}

function addProject() {
    resetNewProject();
    showDialogForm.value = true;
}

function closeDialog() {
    selectedTemplate.value = {};
    uploadedFiles.value = {};
    blankBackground.value = false;
    showDialogForm.value = false;
    resetNewProject();
}

function resetNewProject() {
    blankBackground.value = false;
    newProject.value = {...newProjectObjTemplate, template: {...newProjectObjTemplate.template}};
}

const BG_DIMENSIONS = {
    L: { width: 1126, height: 796 },
    P: { width: 796, height: 1126 },
};

function setTemplateImage(project) {
    if (selectedTemplate.value?.id) {
        return api.copyTemplateImageFromLibrary(project.id, selectedTemplate.value.id);
    }
    if (uploadedFiles.value?.[0] !== undefined) {
        return api.uploadProjectTemplateImage(project.id, uploadedFiles.value);
    }
    return Promise.resolve({ filename: null });
}

function finishProjectCreation(project, filename) {
    newProject.value.template.background_file = filename;
    api.createProjectTemplate(project.id, newProject.value.template).then(
        () => router.push({ name: 'project_edit', params: { project_id: project.id } })
    );
}

function createProject() {
    if (!blankBackground.value && !selectedTemplate.value?.id && uploadedFiles.value?.[0] === undefined) {
        window.alert('Выберите фон из библиотеки, загрузите изображение шаблона или нажмите «Без фона».');
        return;
    }

    let parentFolderId = currentFolderId.value;
    api.createProject(newProject.value.name, parentFolderId).then(project => {
        newProject.value.template.name = 'Шаблон для ' + project.name;

        if (blankBackground.value) {
            finishProjectCreation(project, null);
            return;
        }

        if (selectedTemplate.value?.id) {
            setTemplateImage(project).then(data => finishProjectCreation(project, data.filename));
            return;
        }

        api.uploadProjectTemplateImage(project.id, uploadedFiles.value).then(data => {
            const dims = BG_DIMENSIONS[newProject.value.template.orientation];

            if (data.width < dims.width || data.height < dims.height) {
                window.alert(
                    `Изображение слишком маленькое (${data.width} x ${data.height}). ` +
                    `Минимальный требуемый размер: ${dims.width} x ${dims.height} пикселей.`
                );
                return;
            }

            const ratioImg = data.width / data.height;
            const ratioReq = dims.width / dims.height;

            if (Math.abs(ratioImg - ratioReq) < 0.01) {
                api.processBackground(project.id, {
                    filename: data.filename,
                    orientation: newProject.value.template.orientation,
                    crop_x: 0,
                    crop_y: 0,
                    crop_width: data.width,
                    crop_height: data.height,
                }).then(result => finishProjectCreation(project, result.filename));
            } else {
                cropProjectId.value = project.id;
                cropFilename.value = data.filename;
                cropImageWidth.value = data.width;
                cropImageHeight.value = data.height;
                cropImageSrc.value = `${api.server}/projects/${project.guid}/images/${data.filename}`;
                showCropDialog.value = true;

                const unwatch = watch(pendingUploadData, (cropResult) => {
                    if (cropResult && cropProjectId.value === project.id) {
                        unwatch();
                        api.processBackground(project.id, cropResult).then(
                            result => finishProjectCreation(project, result.filename)
                        );
                    }
                });
            }
        });
    });
}

function onCropApply(cropData) {
    pendingUploadData.value = cropData;
    showCropDialog.value = false;
}

function onCropCancel() {
    showCropDialog.value = false;
    if (cropProjectId.value) {
        cropProjectId.value = null;
    }
}

// Folder rename/delete dialogs
function openRenameFolderDialog(folder) {
    contextFolder.value = folder;
    selectedFolder.value = folder;
    newFolderName.value = folder.name;
    confirm.require({
        group: 'renameFolderDialog',
        header: 'Переименовать папку',
        icon: 'pi pi-info-circle',
        acceptClass: 'p-button-primary',
        acceptLabel: 'Переименовать',
        rejectLabel: 'Отмена',
        accept: () => {
            api.renameFolder(folder.id, newFolderName.value).then(() => loadData());
        },
        reject: () => {}
    });
}

function openDeleteFolderDialog(folder) {
    contextFolder.value = folder;
    confirm.require({
        group: 'deleteFolderDialog',
        header: 'Удалить папку',
        message: 'Вы действительно хотите удалить папку "' + folder.name + '"?',
        icon: 'pi pi-info-circle',
        acceptClass: 'p-button-danger',
        acceptLabel: 'Удалить',
        rejectLabel: 'Отмена',
        accept: () => {
            api.deleteFolder(folder.id).then(() => loadData());
        },
        reject: () => {}
    });
}

// ===== Data loading =====

function loadData() {
    loading.value = true;
    api.getExplorerData().then(data => {
        allProjects.value = data.projects || [];
        folderTree.value = data.folders || [];

        // Build flat folder map
        const flat = {};
        const flattenFolders = (folders) => {
            for (const f of folders) {
                flat[f.id] = f;
                if (f.children && f.children.length) {
                    flattenFolders(f.children);
                }
            }
        };
        flattenFolders(data.folders || []);

        // Also include parent_id references that might not be in the tree
        if (data.folders_flat) {
            for (const [id, folder] of Object.entries(data.folders_flat)) {
                if (!flat[id]) {
                    flat[id] = folder;
                }
            }
        }

        foldersFlat.value = flat;
        loading.value = false;
    }).catch(err => {
        console.error('Failed to load explorer data:', err);
        loading.value = false;
    });
}

// Global folder context menu handler (via EventBus)
function onFolderContextMenu({ folder, event }) {
    contextFolder.value = folder;
    selectedFolder.value = folder;
    nextTick(() => {
        if (menuFolder.value) {
            menuFolder.value.show(event);
        }
    });
}

// ===== Init =====
EventBus.$on(events.FOLDER_CONTEXT_MENU, onFolderContextMenu);
EventBus.$on(events.PROJECT_SHORTCUTS_UPDATED, refreshFavorites);

onBeforeUnmount(() => {
    EventBus.$off(events.FOLDER_CONTEXT_MENU, onFolderContextMenu);
    EventBus.$off(events.PROJECT_SHORTCUTS_UPDATED, refreshFavorites);
});

loadData();
</script>

<style lang="scss">
/* ===== Explorer Layout ===== */
.explorer-layout {
    display: flex;
    height: calc(100vh - 52px);
    gap: 0;
    margin: -28px -32px -24px;
    overflow: hidden;
}

/* ===== Left Sidebar (in-page) ===== */
.explorer-sidebar {
    width: 260px;
    min-width: 260px;
    background: #f8f9fa;
    border-right: 1px solid #dee2e6;
    display: flex;
    flex-direction: column;
    padding: 16px 0;
    overflow-y: auto;
    flex-shrink: 0;
}

.sidebar-section {
    padding: 0 12px;
    margin-bottom: 16px;
}

.sidebar-section-title {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6c757d;
    padding: 0 8px;
    margin-bottom: 6px;
}

.sidebar-section-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.sidebar-small-btn {
    background: none;
    border: none;
    color: #6c757d;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.7rem;
}

.sidebar-small-btn:hover {
    background: #e9ecef;
    color: #495057;
}

.sidebar-nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 12px;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;
    color: #495057;
    text-align: left;
    transition: all 0.15s ease;
    margin-bottom: 2px;
}

.sidebar-nav-item:hover {
    background: #e9ecef;
    color: #212529;
}

.sidebar-nav-item.active {
    background: #0059a9;
    color: white;
}

.sidebar-nav-item i {
    width: 18px;
    text-align: center;
    font-size: 0.95rem;
}

.sidebar-folder-list {
    margin-top: 4px;
}

.sidebar-empty {
    padding: 6px 12px;
    color: #adb5bd;
    font-size: 0.85rem;
}

.sidebar-spacer {
    flex: 1;
}

.sidebar-actions {
    padding: 8px 12px;
    border-top: 1px solid #dee2e6;
}

.sidebar-action-btn {
    width: 100%;
}

/* ===== Folder Tree Items (inline styles handled via render) ===== */

.folder-tree-item {
    user-select: none;
}

.folder-row {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 5px 12px;
    cursor: pointer;
    border-radius: 4px;
    transition: background 0.15s;
    position: relative;
}

.folder-row:hover {
    background: #e9ecef;
}

.folder-row-active {
    background: #dbeafe;
    color: #0059a9;
}

.folder-row-active:hover {
    background: #c7dffc;
}

.folder-toggle {
    width: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.folder-toggle i {
    font-size: 0.65rem;
    color: #6c757d;
}

.folder-toggle-hidden {
    visibility: hidden;
}

.folder-icon {
    font-size: 0.85rem;
    color: #ffc107;
    flex-shrink: 0;
}

.folder-row-active .folder-icon {
    color: #0059a9;
}

.folder-name {
    font-size: 0.85rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
}

.folder-add-sub-btn {
    opacity: 0;
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 3px;
    color: #6c757d;
    font-size: 0.65rem;
    flex-shrink: 0;
    transition: opacity 0.15s;
}

.folder-row:hover .folder-add-sub-btn {
    opacity: 1;
}

.folder-add-sub-btn:hover {
    background: #dee2e6;
    color: #495057;
}

.folder-children {
    /* children are indented via paddingLeft in render */
}

/* ===== Right Panel ===== */
.explorer-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 20px 28px;
    overflow: hidden;
    min-width: 0;
}

/* Breadcrumbs */
.breadcrumbs {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.82rem;
    color: #6c757d;
    margin-bottom: 12px;
    flex-wrap: wrap;
}

.breadcrumb-item {
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 4px;
    transition: background 0.15s;
    white-space: nowrap;
}

.breadcrumb-item:hover {
    background: #e9ecef;
    color: #495057;
}

.breadcrumb-home i {
    margin-right: 4px;
}

.breadcrumb-active {
    color: #212529;
    font-weight: 600;
    cursor: default;
}

.breadcrumb-active:hover {
    background: transparent;
}

.breadcrumb-separator {
    color: #adb5bd;
}

/* Toolbar */
.explorer-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
}

.toolbar-search {
    position: relative;
    flex: 1;
    min-width: 200px;
    max-width: 400px;
}

.search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #adb5bd;
    font-size: 0.85rem;
}

.search-input {
    width: 100%;
    padding: 8px 32px 8px 34px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    font-size: 0.88rem;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;
}

.search-input:focus {
    border-color: #0059a9;
    box-shadow: 0 0 0 2px rgba(0, 89, 169, 0.15);
}

.search-clear {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    color: #6c757d;
    padding: 4px;
    border-radius: 50%;
}

.search-clear:hover {
    background: #e9ecef;
    color: #212529;
}

.toolbar-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.view-toggle {
    display: flex;
    margin-left: 8px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    overflow: hidden;
}

.view-toggle .p-button {
    border-radius: 0 !important;
    border: none !important;
}

/* Section header */
.section-header {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 16px;
}

.section-title {
    font-size: 1.3rem;
    font-weight: 700;
    color: #212529;
    margin: 0;
}

.section-count {
    font-size: 0.85rem;
    color: #6c757d;
}

/* Loading */
.explorer-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
    color: #6c757d;
    font-size: 1rem;
    gap: 10px;
}

/* Empty state */
.explorer-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 0;
    color: #6c757d;
}

.empty-icon {
    font-size: 3rem;
    margin-bottom: 16px;
    opacity: 0.5;
}

.explorer-empty p {
    font-size: 1rem;
}

/* ===== List View ===== */
.list-view {
    overflow-y: auto;
    flex: 1;
    border: 1px solid #e9ecef;
    border-radius: 8px;
}

.list-header {
    display: flex;
    align-items: center;
    padding: 10px 16px;
    background: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
    font-size: 0.78rem;
    font-weight: 700;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    position: sticky;
    top: 0;
    z-index: 1;
}

.list-row {
    display: flex;
    align-items: center;
    padding: 6px 16px;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: background 0.12s;
}

.list-row:hover {
    background: #f8f9ff;
}

.list-row-selected {
    background: #eef2ff;
}

.list-cell {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 0 8px;
}

.list-cell-favorite {
    flex: 0 0 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
}

.favorite-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    color: #ced4da;
    font-size: 0.95rem;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s, background 0.15s;
}

.favorite-btn:hover {
    background: #f1f3f5;
    color: #ffc107;
}

.favorite-btn-active {
    color: #ffc107;
}

.favorite-btn-active:hover {
    color: #e0a800;
}

.list-cell-preview {
    flex: 0 0 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
}

.list-preview-img {
    width: 44px;
    height: 32px;
    object-fit: contain;
    border-radius: 3px;
    background: #f0f0f0;
    display: block;
}

.list-preview-placeholder {
    width: 44px;
    height: 32px;
    background: #f0f0f0;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #adb5bd;
    font-size: 0.8rem;
}

.list-cell-name {
    flex: 3;
    min-width: 0;
    text-align: left;
}

.project-name-text {
    font-weight: 500;
    color: #212529;
}

.list-cell-path {
    flex: 2;
    min-width: 0;
    font-size: 0.85rem;
    color: #6c757d;
}

.list-cell-date {
    flex: 1.5;
    min-width: 100px;
    font-size: 0.85rem;
    color: #6c757d;
}

.list-cell-source {
    flex: 1;
    min-width: 80px;
}

.source-tag .p-tag-value {
    font-size: 0.72rem;
}

.source-none {
    color: #adb5bd;
    font-size: 0.85rem;
}

.list-cell-public {
    flex: 0.6;
    min-width: 50px;
    text-align: center;
}

.public-yes {
    color: #28a745;
    font-size: 1rem;
}

.public-no {
    color: #adb5bd;
    font-size: 1rem;
}

.list-cell-records {
    flex: 0.7;
    min-width: 50px;
    text-align: center;
    font-size: 0.85rem;
    color: #6c757d;
}

.list-cell-actions {
    flex: 0.5;
    min-width: 40px;
    text-align: center;
}

.action-menu-btn {
    width: 28px;
    height: 28px;
}

/* ===== Tile View ===== */
.tile-view {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    grid-auto-rows: max-content;
    align-content: start;
    gap: 16px;
    overflow-y: auto;
    flex: 1;
    padding: 4px 0;
}

.tile-card {
    border: 1px solid #e9ecef;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    background: white;
    display: flex;
    flex-direction: column;
    height: 280px;
}

.tile-card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border-color: #c7dffc;
    transform: translateY(-2px);
}

.tile-card-selected {
    border-color: #0059a9;
    box-shadow: 0 0 0 2px rgba(0, 89, 169, 0.2);
}

.tile-preview {
    width: 100%;
    height: 160px;
    flex-shrink: 0;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.tile-preview img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s;
}

.tile-card:hover .tile-preview img {
    transform: scale(1.03);
}

.tile-img-landscape {
    object-fit: contain;
}

.tile-img-portrait {
    object-fit: contain;
}

.tile-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #adb5bd;
    font-size: 0.8rem;
}

.tile-placeholder i {
    font-size: 2rem;
}

.hidden {
    display: none;
}

.tile-info {
    padding: 10px 12px 12px;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    text-align: left;
}

.tile-name {
    font-weight: 600;
    font-size: 0.9rem;
    color: #212529;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-bottom: 4px;
    text-align: left;
}

.tile-meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 0.78rem;
    color: #6c757d;
    text-align: left;
}

.tile-path {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tile-date {
    color: #adb5bd;
}

.tile-badges {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
}

.source-tag-sm .p-tag-value {
    font-size: 0.65rem;
}

.public-yes-sm {
    color: #28a745;
    font-size: 0.8rem;
}

.tile-action-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    opacity: 0;
    transition: opacity 0.2s;
    background: rgba(255, 255, 255, 0.9);
    width: 28px;
    height: 28px;
}

.tile-favorite-btn {
    position: absolute;
    top: 6px;
    left: 6px;
    width: 28px;
    height: 28px;
    border: none;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #ced4da;
    font-size: 0.9rem;
    opacity: 0;
    transition: opacity 0.2s, color 0.15s, background 0.15s;
}

.tile-card:hover .tile-favorite-btn,
.tile-card:focus-within .tile-favorite-btn {
    opacity: 1;
}

.tile-favorite-btn-active {
    opacity: 1;
    color: #ffc107;
}

.tile-favorite-btn:hover {
    color: #ffc107;
    background: white;
}

.tile-favorite-btn-active:hover {
    color: #e0a800;
}

@media (hover: none) {
    .tile-favorite-btn {
        opacity: 0.85;
    }
}

.tile-card:hover .tile-action-btn,
.tile-card:focus-within .tile-action-btn {
    opacity: 1;
}

@media (hover: none) {
    .tile-action-btn {
        opacity: 0.7;
    }
    .tile-card:hover .tile-action-btn {
        opacity: 1;
    }
}

/* ===== Preserved styles from original ===== */
.preview {
    width: 150px;
    height: 150px;
    background: lightgray;
}

.preview img {
    max-width: 150px;
    max-height: 150px;
    margin: auto;
    display: block;
    padding: 10px;
}

.blank-bg-hint {
    padding: 10px;
    font-size: 0.8rem;
    color: #475569;
    text-align: center;
}

.my-bg .window {
    z-index: 10400 !important;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
    .explorer-sidebar {
        display: none;
    }
    .explorer-content {
        padding: 16px;
    }
    .tile-view {
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    }
    .list-cell-path,
    .list-cell-date,
    .list-cell-source,
    .list-cell-records {
        display: none;
    }
}
</style>
